'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { Project } from '@/lib/data';
import { runWhenIdle } from '@/lib/utils';

// Loose alias to avoid pulling the gsap types module into the entry chunk
// graph just for one type annotation.
type TweenVars = Record<string, unknown>;

const FEATURED_LIMIT = 5;

interface PortfolioSectionProps {
  projects?: Project[]; // Optional fallback projects
}

export function PortfolioSection({ projects: fallbackProjects = [] }: PortfolioSectionProps) {
  const sectionRef = useRef<HTMLSelectElement | HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const [projects, setProjects] = useState<Project[]>(fallbackProjects.slice(0, FEATURED_LIMIT));
  const [loading, setLoading] = useState(true);
  const [animationReady, setAnimationReady] = useState(false);

  // Image load/error state is tracked imperatively via DOM classes instead of
  // React state. Re-rendering the gallery on every image load was racing with
  // GSAP's `pin: true` DOM mutations and tripping React 19's removeChild
  // reconciliation crash.
  const handleImageLoad = useCallback((projectId: string) => {
    const root = galleryRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-project-id="${projectId}"]`);
    el?.setAttribute('data-loaded', 'true');
  }, []);

  const handleImageError = useCallback((projectId: string) => {
    const root = galleryRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-project-id="${projectId}"]`);
    el?.setAttribute('data-loaded', 'true');
    el?.setAttribute('data-error', 'true');
  }, []);

  // Lazy-load Firestore client off the critical path. If fallback data is
  // present the homepage renders instantly from data.ts and we DO NOT replace
  // the project list afterwards — swapping `projects` after GSAP ScrollTrigger
  // has pinned the gallery causes React 19 to crash with `removeChild` on
  // unmount (GSAP has moved the pinned children into pin-spacer wrappers that
  // React no longer owns). Firestore-only refreshes are deferred to a future
  // version that tears down ScrollTrigger before re-rendering.
  useEffect(() => {
    if (fallbackProjects.length > 0) {
      setProjects(fallbackProjects.slice(0, FEATURED_LIMIT));
      setLoading(false);
      return;
    }

    let cancelled = false;

    runWhenIdle(async () => {
      try {
        const { getPublicProjects } = await import('@/lib/services/publicProjectService');
        const firestoreProjects = await getPublicProjects();
        if (!cancelled && firestoreProjects.length > 0) {
          setProjects(firestoreProjects.slice(0, FEATURED_LIMIT));
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 2500, 1200);

    return () => {
      cancelled = true;
    };
  }, [fallbackProjects]);

  // Wait for projects to load before enabling animations
  useEffect(() => {
    if (!loading && projects.length > 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setAnimationReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, projects.length]);

  // GSAP animations - only run when animation is ready. GSAP + ScrollTrigger
  // are dynamic-imported (TASK-4) so the homepage entry chunk doesn't carry
  // them — they load alongside the portfolio data on idle.
  useEffect(() => {
    if (!animationReady || !galleryRef.current || typeof window === 'undefined') return;

    const isMobile = window.innerWidth < 768;
    const gallery = galleryRef.current;
    const images = gallery.querySelectorAll('.overlapping-image');

    if (images.length === 0) return;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap, ScrollTrigger } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      // Set initial height for each image container
      const setImagesHeight = () => {
        gsap.set(images, { height: window.innerHeight });
      };
      setImagesHeight();
      window.addEventListener('resize', setImagesHeight);

      // Create pinning and scale animations for each image
      images.forEach((pImage, i, arr) => {
        if (i < arr.length - 1) {
          const durationMultiplier = arr.length - i - 1;
          const inner = pImage.querySelector('.overlapping-image-inner') as HTMLElement;

          if (!inner) return;

          // Pin animation
          ScrollTrigger.create({
            trigger: pImage,
            start: () => {
              const centerPin = (window.innerHeight - inner.offsetHeight) / 2;
              return `top +=${centerPin}`;
            },
            end: () => {
              const durationHeight = (pImage as HTMLElement).offsetHeight * durationMultiplier +
                ((pImage as HTMLElement).offsetHeight - inner.offsetHeight) / 2;
              return `+=${durationHeight}`;
            },
            pin: true,
            pinSpacing: false,
            scrub: true,
          });

          // Scale and blur animation
          const animationProps: TweenVars = {
            scale: 0.75,
            opacity: 1,
            zIndex: 0,
            duration: 1,
            ease: 'none',
          };

          if (!isMobile) {
            animationProps.filter = 'blur(10px)';
          }

          ScrollTrigger.create({
            trigger: pImage,
            start: () => {
              const centerPin = (window.innerHeight - inner.offsetHeight) / 2;
              return `top +=${centerPin}`;
            },
            end: () => {
              const durationHeight = (pImage as HTMLElement).offsetHeight +
                ((pImage as HTMLElement).offsetHeight - inner.offsetHeight) / 2;
              return `+=${durationHeight}`;
            },
            scrub: true,
            animation: gsap.to(inner, animationProps),
          });
        }
      });

      return () => {
        window.removeEventListener('resize', setImagesHeight);
      };
    }, galleryRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [animationReady]);

  // Loading state
  if (loading) {
    return (
      <section style={{ minHeight: '100vh' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '24px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: '3px solid rgba(255, 255, 255, 0.1)',
              borderTopColor: '#ffffff',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
            Loading projects...
          </p>
          {/* @keyframes spin defined globally in globals.css */}
        </div>
      </section>
    );
  }

  // Empty state
  if (projects.length === 0) {
    return (
      <section>
        <div
          className="flex flex-col items-center justify-center"
          style={{ minHeight: '50vh', padding: '80px 20px' }}
        >
          <p className="text-center text-white/50 text-lg mb-8">
            No projects available yet.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-sm font-medium text-white transition-all duration-300 hover:bg-white hover:text-black"
            style={{ paddingLeft: '50px', paddingRight: '50px', paddingTop: '20px', paddingBottom: '20px' }}
          >
            Get In Touch
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className="relative z-20 pt-16 pb-24 md:pt-24 md:pb-32 w-full overflow-hidden"
    >
      <h2 className="sr-only">Featured work</h2>
      {/* Overlapping Gallery */}
      <div ref={galleryRef} className="overlapping-gallery relative">
        {projects.map((project, index) => (
          <div
            key={project.id}
            className="overlapping-image"
          >
            <div className="overlapping-image-inner trigger-item">
              {/* Image Mask */}
              <div className="img-mask">
                <Link
                  href={`/datacode-portfolio/${project.slug}`}
                  className="slide-link"
                  aria-label={project.title}
                  data-cursor-label="View"
                />
                <div
                  className="section-image portfolio-image-slot"
                  data-project-id={String(project.id)}
                  data-loaded="false"
                  data-error="false"
                >
                  {/* Loading placeholder (always rendered, hidden via [data-loaded]) */}
                  <div className="portfolio-image-placeholder">
                    <div className="portfolio-image-spinner" />
                  </div>
                  {/* Error state (always rendered, shown via [data-error]) */}
                  <div className="portfolio-image-error">
                    <span>Image unavailable</span>
                  </div>
                  <Image
                    src={project.image}
                    alt={project.title}
                    fill
                    className="item-image portfolio-image-actual"
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1280px"
                    priority={index === 0}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    onLoad={() => handleImageLoad(String(project.id))}
                    onError={() => handleImageError(String(project.id))}
                  />
                </div>
              </div>
              {/* Caption Overlay */}
              <div className="slide-caption">
                <div className="slide-index">
                  <span>{String(index + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}</span>
                </div>
                <div className="slide-title font-six-caps">
                  <span>{project.title}</span>
                </div>
                <div className="slide-date">
                  <span>{project.year}</span>
                </div>
                <div className="slide-cat">
                  <span>{project.category}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

    </section>
  );
}
